import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { SectionStage } from '../components/SectionStage';
import { SectionLabel, SectionHeading, PrimaryButton } from '../components/ui/UI';
const architectureSteps = [{
  n: '01',
  title: 'Model Request',
  body: 'The frontier model reasons over approved context and requests a tool call (e.g., "query_evidence_graph" or "request_control_validation").'
}, {
  n: '02',
  title: 'Policy Check',
  body: 'Periscan intercepts the request. The Policy Engine verifies tenant policy, user role, scope, safety level, and approval requirements.'
}, {
  n: '03',
  title: 'Safe Execution',
  body: "Approved requests are executed through Periscan's existing validation modules, API connectors, or internal runners. The model never touches the network directly."
}, {
  n: '04',
  title: 'Redacted Output',
  body: 'Results are captured as evidence. Sensitive data is redacted before the output is returned to the model for further reasoning.'
}];
const modes = [{
  title: 'Plan-Only Mode',
  desc: 'The model can reason and propose validation plans, but cannot execute tools. Ideal for threat advisory analysis and executive summaries.'
}, {
  title: 'Read-Only Evidence',
  desc: 'The model can query Periscan\'s normalized evidence graph to answer questions like "What do we know?" without touching customer systems.'
}, {
  title: 'Safe Validation',
  desc: 'The model can request approved validation missions (e.g., external exposure checks, AI app baseline tests) after policy approval.'
}, {
  title: 'Guided Remediation',
  desc: 'The model can draft Jira tickets, IaC pull requests, or WAF rules in monitor mode. No auto-apply without explicit policy allowance.'
}, {
  title: 'High-Assurance',
  desc: 'For regulated environments. Everything requires human approval. No automatic execution, strict redaction, and detailed audit trails.'
}];
const safetyRules = ['No direct network access.', 'No arbitrary shell commands.', 'No raw secrets sent to the model.', 'All tool calls are policy-checked.', 'All tool calls are audited.', 'All outputs are redacted.', 'All sensitive actions are approval-gated.', 'No risk can be marked fixed without real verification evidence.'];
export function FrontierGateway() {
  return <>
      <PageHero eyebrow="Frontier Gateway" title="Frontier cyber models" titleMuted="need guardrails." description="Periscan lets enterprises use powerful security models without giving them direct access to the network. The model thinks. Periscan controls. Evidence proves." bg="none" data-id="element-1589" />

      {/* Architecture */}
      <SectionStage fx="circuit" data-id="element-1590">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1591">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1592">
            <SectionLabel number="01" label="Architecture" className="mb-8" data-id="element-1593" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1594">
              <div className="lg:col-span-8" data-id="element-1595">
                <SectionHeading size="xl" data-id="element-1596">
                  The control layer for
                  <br data-id="element-1597" />
                  <span className="text-white/40" data-id="element-1598">AI-driven security.</span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-4 lg:pt-4" data-id="element-1599">
                <p className="text-base text-white/60 leading-relaxed" data-id="element-1600">
                  Frontier Gateway sits between powerful cyber models and your
                  environment. Models can reason and plan, but all interaction
                  goes through typed, policy-controlled Periscan tools.
                </p>
              </div>
            </div>

            <div className="flex flex-col" data-id="element-1601">
              {architectureSteps.map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.04
            }} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 border-b border-white/5 last:border-0" data-id="element-1602">
                  <div className="md:col-span-2" data-id="element-1603">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-1604">
                      {step.n}
                    </span>
                  </div>
                  <div className="md:col-span-3" data-id="element-1605">
                    <h3 className="text-xl font-semibold text-white tracking-tight" data-id="element-1606">
                      {step.title}
                    </h3>
                  </div>
                  <div className="md:col-span-7" data-id="element-1607">
                    <p className="text-base text-white/60 leading-relaxed" data-id="element-1608">
                      {step.body}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>
      </SectionStage>

      {/* Modes of Operation */}
      <SectionStage fx="matrix" data-id="element-1609">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1610">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1611">
            <SectionLabel number="02" label="Modes of Operation" className="mb-8" data-id="element-1612" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1613">
              <div className="lg:col-span-8" data-id="element-1614">
                <SectionHeading size="xl" data-id="element-1615">
                  Bounded autonomy.
                  <br data-id="element-1616" />
                  <span className="text-white/40" data-id="element-1617">
                    You set the leash length.
                  </span>
                </SectionHeading>
              </div>
            </div>

            <div className="border-t border-white/5" data-id="element-1618">
              {modes.map((mode, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.04
            }} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-6 border-b border-white/5 last:border-0" data-id="element-1619">
                  <div className="md:col-span-1" data-id="element-1620">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-1621">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="md:col-span-4" data-id="element-1622">
                    <h3 className="text-lg font-semibold text-white tracking-tight" data-id="element-1623">
                      {mode.title}
                    </h3>
                  </div>
                  <div className="md:col-span-7" data-id="element-1624">
                    <p className="text-base text-white/60 leading-relaxed" data-id="element-1625">
                      {mode.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>
      </SectionStage>

      {/* Safety Rules */}
      <SectionStage fx="constellation" data-id="element-1626">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1627">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1628">
            <SectionLabel number="03" label="Safety & Control" className="mb-8" data-id="element-1629" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1630">
              <div className="lg:col-span-7" data-id="element-1631">
                <SectionHeading size="xl" data-id="element-1632">
                  The hacker on your side.
                  <br data-id="element-1633" />
                  <span className="text-white/40" data-id="element-1634">
                    With a logbook and a kill switch.
                  </span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-5 lg:pt-4" data-id="element-1635">
                <div className="p-8 rounded-2xl bg-[#1a8fd4]/5 border border-[#1a8fd4]/20" data-id="element-1636">
                  <h4 className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-1637">
                    Hard Constraints
                  </h4>
                  <ul className="space-y-4" data-id="element-1638">
                    {safetyRules.map((rule, i) => <li key={i} className="flex items-start gap-3 text-sm text-white/80" data-id="element-1639">
                        <span className="w-1 h-1 rounded-full bg-[#1a8fd4] flex-shrink-0 mt-2" data-id="element-1640" />
                        <span data-id="element-1641">{rule}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-12 flex justify-start" data-id="element-1642">
              <PrimaryButton href="/early-access" size="lg" data-id="element-1643">
                Request Gateway Access
              </PrimaryButton>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}